import { Input, message, Radio, Tooltip } from 'antd'
import classNames from 'classnames'
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import InputArea from '~/components/pages/ai/InputArea'
import { MyForm } from '~/components/ui/MyForm'
import {
  GET_AI_MODELCODE_SIZES,
  GET_EXPECTED_SECONDS,
  GET_AI_MODELCODE_DEFAULT_OPTIONS,
  GET_CONTROLNET_PREFINE_OPTIONS,
} from '~/graphqls/gqls'
import {
  getAITotalInfo_pictureGraph_aiArtworkTotalInfo,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu,
  AiCreateArtworkModel,
  getAIMemberList_accountModule_userMemberCardList,
  MemberCardRightTypeEnum,
  AiCreateArtworkPattern,
  getExpectedSeconds,
  getExpectedSecondsVariables,
  getExpectedSeconds_pictureGraph_aiArtworkTotalInfo_expectedSeconds,
  AiArtworkOptionInput,
  AiArtworkModelFusionInput,
  getAIModelcodeSizes,
  getAIModelcodeSizesVariables,
  getAIModelcodeDefaultOptions,
  getAIModelcodeDefaultOptionsVariables,
  SuperType,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_recommendedModelFusionOptions,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_samplerOptions,
  PretreatmentMethod,
  getControlnetPrefineOptions,
  getControlnetPrefineOptionsVariables,
  GPTVisibleState,
} from '~/graphqls/types'
import { QuestionCircleOutlined } from '@ant-design/icons'
import Stepper from '~/components/pages/ai/Stepper'

import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import styles from './AiForm.module.css'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import { clearStorage, getStorage, timeWithUint, toCDNImage } from '~/utils'
import {
  MultiFileUpload,
  MyUploadFile,
} from '~/components/business/MultiFileUpload'
import TiledSelect, { ModelThemeProps } from '~/components/pages/ai/TiledSelect'
import { MySwitch } from '~/components/ui/MySwitch'
import { MySlider } from '~/components/ui/MySlider'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { observer } from 'mobx-react'
import { useStores } from '~/hooks/useStores'
import PaintWithUpload from '../lab/PaintWithUpload'
import { MyLink } from '~/components/ui/MyLink'
import { MyIcon } from '~/components/ui/MyIcon'
import Router from 'next/router'
import useOptions from './useOptions'
import { PictureCardUpload } from '~/components/business/PictureCardUpload'
import { ModelFusionSelect, ModelFusionDataProps } from './ModelFusionSelect'
import GridSelect from '../GridSelect'
import { StyleDecorationSelect } from './StyleDecorationSelect'
import ModelCodeSelect from '../lab/ModelCodeSelect'
import { controlnetPatternArr } from '~/configs/ai'
import PreProcessSelect, {
  options as preProcessOptions,
} from '../lab/PreProcessSelect'
import PreProcessSwitch from '../lab/PreProcessSwitch'
import { Prompt } from './Prompt'
import useFormInstance from 'antd/lib/form/hooks/useFormInstance'

interface FormParamsProps {
  prompt?: string
  size?: string
  width?: number
  length?: number
  model?: AiCreateArtworkModel
  initImageUrl?: string
  creativityDegree?: number
  num?: number
  promptOptimize?: number
  accelarateTimes?: number
  accelerate?: boolean
  maskImageUrl?: string
  maskZoneImageUrl?: string
  pattern: AiCreateArtworkPattern
  modelCode: number
  ucPrompt?: string
  doRepairTheHand?: boolean
  styleDecorationOptions?: AiArtworkOptionInput[]
  character?: string[]
  modelFusion?: AiArtworkModelFusionInput[]
  initWidth?: number
  initHeight?: number
  superType?: SuperType | null
  cfg?: number | null
  seed?: string | null
  samplerIndex?: number | null
  steps?: number | null
  /** 线稿上色模式特有参数：预处理方式 */
  pretreatmentMethod?: PretreatmentMethod
  /** 百度统计事件 */
  trackEventLabel?: string | null
  isGPT?: boolean
}

export interface AiFormHandles {
  reset: () => void
  getParams: () => Promise<FormParamsProps>
}

// 默认创意度
const DEFAULT_CREATIVITY_DEGREE = 50

const AiFormMain: React.ForwardRefRenderFunction<
  AiFormHandles,
  {
    options: getAIMenu_pictureGraph_aiCreateArtworkOptionMenu
    totalInfo: getAITotalInfo_pictureGraph_aiArtworkTotalInfo
    memberList: getAIMemberList_accountModule_userMemberCardList[]
    /** 锁定model版本 */
    lockModel?: AiCreateArtworkModel
    /** 锁定modelCode */
    lockModelCode?: number
    /** 锁定pattern */
    lockPattern?: AiCreateArtworkPattern
    gptVisibleState: GPTVisibleState
    setGptVisibleState: Dispatch<SetStateAction<GPTVisibleState>>
  }
> = (
  {
    options: _options,
    totalInfo,
    memberList,
    lockModel,
    lockModelCode,
    lockPattern = AiCreateArtworkPattern.GENERAL,
    gptVisibleState,
    setGptVisibleState,
  },
  ref
) => {
  const { validateIsLogin } = useValidateAuth()
  const { store } = useStores()
  const [options, setOptions] = useState(_options)

  const [selectModel, setSelectModel] = useState(
    lockModel ?? AiCreateArtworkModel.ANIME_DIFFUSION
  )

  const [form] = MyForm.useForm<{ size: string; [key: string]: any }>()

  const selectSize = MyForm.useWatch('size', form)
  const selectedResolution = MyForm.useWatch('resolution', form)
  const selectInitImageUrl = MyForm.useWatch('initImageUrl', form)
  const selectedControlPrefine = MyForm.useWatch('controlnetPrefine', form)
  // const selectSampler = MyForm.useWatch('sampler', form)
  // 记录配置项是否全部加载，解决再来一张时，因为curmode的变化，重新请求一些配置项，导致选项匹配问题和cfg等存在默认值的表单被默认值覆盖
  const isOptionsRendered = useRef(false)
  // 这是首页右边快捷再来一张
  const isOptionsRenderedForHomepage = useRef(false)
  /** 底图提示比例 */
  const [initImageSize, setInitImageSize] = useState<string | null>(null)
  /** 涂抹模式专用 */
  const paintRef = useRef<any>()
  const [maskImageUrl, setMaskImageUrl] = useState('')
  const [doRepairTheHand, setDoRepairTheHand] = useState(false)
  /** 画面模式 */
  const pattern = lockPattern
  /** 高级设置 */
  const [hasAdvance, setHasAdvance] = useState(false)
  /** 创意度 */
  const [_creativityDegree, _setCreativityDegree] = useState(
    DEFAULT_CREATIVITY_DEGREE
  )
  const creativityDegreeTouched = useRef(false)
  const setCreativityDegree = (n: number) => {
    creativityDegreeTouched.current = true
    _setCreativityDegree(n)
  }
  /** 是否使用描述词智能优化 */
  const [promptOptimize, setPromptOptmize] = useState(false)
  /** 作画数量 */
  const [paintTimes, setPaintTimes] = useState(1)
  /** 加速生成 */
  const [accelerate, setAccelerate] = useState(false)
  /** 加速次数 */
  const [accelarateTimes, setAccelerateTimes] = useState(1)
  /** 某些 pattern（当前为controlnet系列） 下可以选择modelCode, 和 model */
  const selectedModelCodeForPattern = MyForm.useWatch(
    'modelCodeForPattern',
    form
  )
  // gpt描述文本
  const [gptContent, setGptContent] = useState('')

  /** controlnet精修options */
  const controlPrefineOptions = useMemo(() => {
    if (!options.controlPrefineOption?.length) return []
    const controlPrefineOption: {
      label: string
      value: SuperType
      price?: number | undefined
    }[] = []
    options.controlPrefineOption.forEach(it => {
      if (it?.superType === SuperType.SUPERSIZE) {
        controlPrefineOption.push({
          label: '不使用精绘',
          value: it.superType,
          price: it?.price || 0,
        })
      } else {
        controlPrefineOption.push({
          label: '精绘',
          value: it?.superType as SuperType,
          price: it?.price || 0,
        })
      }
    })
    return controlPrefineOption
  }, [options.controlPrefineOption])

  /** 模型主题 */
  const [selectModelTheme, setSelectModelTheme] = useState<ModelThemeProps[]>(
    []
  )
  /** 风格选择 */
  const [styleDecoration, setStyleDecoration] = useState<
    AiArtworkOptionInput[]
  >([])
  /** Lora模型 */
  const [modelFusion, setModelFusion] = useState<ModelFusionDataProps[]>([])

  useImperativeHandle(ref, () => ({
    reset: () => {
      setPaintTimes(1)
      setAccelerateTimes(1)
      setAccelerate(false)
      setGptContent('')
    },
    getParams: async () => {
      let formData
      try {
        formData = await form.validateFields()
      } catch (err: any) {
        // 跳转到第一个错误
        const fieldId = err?.errorFields?.[0]?.name?.[0]
        const $el = document.getElementById(fieldId)
        $el && $el?.scrollIntoView()

        // antd的uplaod做的有问题，他把input.display:none了，没法正确scroll, 这里特殊处理下
        if (fieldId === 'initImageUrl') {
          document.documentElement.scrollTop = 0
        }
        throw err
      }

      let initImageUrl = ''
      // 两种情况，1 深度图像转换initImageUrl用了PictureCardUpload组件，initImageUrl是string
      // 2. 普通模式initImageUrl用了MultipleUpload组件，是 Array
      if (typeof formData.initImageUrl !== 'string') {
        // 需要底图全部上传完成
        if (
          !(formData.initImageUrl || []).every((item: any) => {
            return item.status === 'done'
          })
        ) {
          message.destroy()
          message.error('请等待图片上传完成后提交')
          return Promise.reject()
        }
        initImageUrl = formData.initImageUrl?.[0]?.url
      } else {
        initImageUrl = formData.initImageUrl
      }

      let maskImageUrl
      let maskZoneImageUrl
      if (pattern === AiCreateArtworkPattern.MASK) {
        const res = await paintRef.current.save()

        if (!res?.[0]) {
          message.destroy()
          message.error('请上传图片')
          return Promise.reject()
        }

        maskImageUrl = res[0]
        maskZoneImageUrl = res[1]
      }

      let width: number = 0
      let height: number = 0
      let superType: SuperType = SuperType.SUPERSIZE
      if (formData.resolution) {
        const [a, b, s] = formData.resolution.split('*')
        width = Number(a)
        height = Number(b)
        superType = s
      }
      let size = formData.size
      let initWidth: number | undefined = undefined // 底图宽
      let initHeight: number | undefined = undefined // 底图高
      if (initImageUrl) {
        await new Promise<void>((resolve, reject) => {
          const img = new Image()
          img.src = initImageUrl
          img.onload = () => {
            initWidth = img.naturalWidth
            initHeight = img.naturalHeight
            resolve()
          }
          img.onerror = () => {
            reject('获取底图宽度时出错')
          }
        })
      }
      /** controlnet 没有尺寸，用底图尺寸处理 size resolution */
      if (controlnetPatternArr.includes(pattern)) {
        size = `${initWidth}:${initHeight}`
        width = initWidth!
        height = initHeight!
        superType = selectedControlPrefine as SuperType
      }

      let model = formData.model
      if (lockModel) {
        model = lockModel
      } else if (controlnetPatternArr.includes(pattern)) {
        model = modelForPattern
      }

      let isGPT = formData.prompt === gptContent

      return {
        character: isSupportLoRA ? formData.character : [],
        modelFusion: isSupportLoRA ? modelFusion : [],
        styleDecorationOptions: styleDecoration,
        prompt: formData.prompt || '修复',
        size: size,
        style: formData.style,
        width: height, // 高
        length: width, // 宽
        superType,
        model,
        initImageUrl: initImageUrl,
        creativityDegree: creativityDegree,
        num: paintTimes,
        promptOptimize: promptOptimize ? 1 : 0,
        accelarateTimes: accelerate ? accelarateTimes : undefined,
        accelerate,
        maskImageUrl,
        maskZoneImageUrl,
        pattern,
        modelCode: curModelCode,
        ucPrompt: formData.ucPrompt,
        doRepairTheHand,
        initWidth,
        initHeight,
        cfg: hasAdvance ? formData.cfg : undefined,
        // 服务端需要字符串类型
        seed: formData.seed ? `${formData.seed}` : undefined,
        pretreatmentMethod: formData.pretreatmentMethod,
        samplerIndex: hasAdvance ? formData.sampler?.[0] : undefined,
        trackEventLabel: formData.trackEventLabel,
        // steps:
        //   hasAdvance && !!formData.sampler?.length
        //     ? formData.samplerstep
        //     : undefined,
        // denoising: formData.denoising,
        isGPT: isGPT,
      }
    },
  }))

  const {
    prompts,
    modelThemeOptions,
    curModelThemeOptions,
    styleDecorationOptions,
    modelFusionOptions,
    characterOptions,
    onRefreshPrompts,
    ddSize,
    ddResolution,
    sdSize,
    sdResolution,
  } = useOptions(options, selectModel)

  /** 预计时间 */
  const [expectedSeconds, setExpectedSeconds] =
    useState<getExpectedSeconds_pictureGraph_aiArtworkTotalInfo_expectedSeconds>(
      totalInfo.expectedSeconds
    )

  /** 通用模型 */
  const isSD = selectModel === AiCreateArtworkModel.STABLE_DIFFUSION
  /** 色彩模型 */
  const isDD = selectModel === AiCreateArtworkModel.DISCO_DIFFUSION
  /** 二次元模型 */
  const isAD = selectModel === AiCreateArtworkModel.ANIME_DIFFUSION

  /** 创意度字段名字使用 创意度 还是 二次元强度 */
  const show2dDensity = isAD && !controlnetPatternArr.includes(pattern)

  /** 可用作画次数 */
  let totalPaintTimes = totalInfo.buyTimes
  /** 权益卡可用创作次数 */
  let createTimes = 0
  /** 权益卡可用加速次数 */
  let memberAccelerateTimes = 0
  memberList.forEach(item => {
    item.holdCardInfo?.forEach(i => {
      if (i?.rightType === MemberCardRightTypeEnum.AI_CREATE) {
        createTimes += i.num || 0
      }
      if (i?.rightType === MemberCardRightTypeEnum.ACCELERATE) {
        memberAccelerateTimes += i.num || 0
      }
    })
  })
  totalPaintTimes += createTimes
  /** 付费的生成次数（积分+权益卡）, 可用于dd模型，免费次数不能用于dd模型 */
  const payPaintTimes = Math.max(totalPaintTimes - totalInfo.freeTimes, 0)

  /** 最大可加速次数 */
  const maxPoint = useMemo(() => {
    const maxCount = expectedSeconds?.queueNum - 1
    return maxCount * paintTimes
  }, [expectedSeconds, paintTimes])

  /** 大模型选项 */
  const modelOptions = useMemo(() => {
    return options.modelSupportOptions.map(i => {
      let pattern2Code = i.modelSupport.reduce<Record<string, number>>(
        (acc, cur) => {
          acc[cur.pattern!] = cur.modelCode!
          return acc
        },
        {}
      ) as Record<AiCreateArtworkPattern, number>
      return {
        ...i,
        pattern2Code: pattern2Code,
      }
    })
  }, [options])

  /** 当前选中模型选项 */
  const [modelForPattern, modelCodeForPattern] = useMemo(() => {
    if (
      !options.patternModelOptions ||
      options.patternModelOptions.length === 0
    )
      return []
    const find = options.patternModelOptions.find(item => {
      return item?.modelCode === selectedModelCodeForPattern
    })
    if (find) {
      return [find.model, find.modelCode]
    }
    return [
      options.patternModelOptions[0].model,
      options.patternModelOptions[0].modelCode,
    ]
  }, [options, selectedModelCodeForPattern])

  const curModel = useMemo(() => {
    const _ = modelOptions.find(item => item?.model === selectModel)
    return _ || modelOptions[0]
  }, [modelOptions, selectModel])

  const curSizeOptions = isDD ? ddSize : sdSize
  const curR = isDD ? ddResolution : sdResolution
  const curResolutionOptions = curR[selectSize] || []
  /** 当前的ModelCode */
  const curModelCode = useMemo(() => {
    if (lockModelCode) return lockModelCode
    if (modelCodeForPattern) return modelCodeForPattern
    /** 根据当前选中的模型主题生成的modelCode, 相比从model选项中拿到的modalCode具有更高的优先级 */
    const modelCodeFromModelTheme = modelThemeOptions.find(
      item => item.name === selectModelTheme?.[0]?.name
    )?.modelCode

    if (modelCodeFromModelTheme) return modelCodeFromModelTheme
    return curModel.pattern2Code[pattern]
  }, [
    modelThemeOptions,
    selectModelTheme,
    curModel,
    pattern,
    modelCodeForPattern,
  ])

  const setSize = () => {
    const size = form.getFieldValue('size')
    if (curSizeOptions.length === 0) {
      return
    }
    const index = curSizeOptions.findIndex(item => item.name === size)
    if (index === -1) {
      form.setFieldsValue({ size: curSizeOptions[0].name })
    }
  }
  useEffect(() => {
    setSize()
  }, [form, curSizeOptions])

  // 没有usecallback 导致curResolutionOptions不是最新
  const setResolution = useCallback(() => {
    if (curResolutionOptions.length === 0) {
      return
    }
    const index = curResolutionOptions.findIndex(
      item =>
        `${item.length}*${item.width}*${item.superType}` === selectedResolution
    )
    if (index === -1) {
      form.setFieldsValue({
        resolution: `${curResolutionOptions[0].length}*${curResolutionOptions[0].width}*${curResolutionOptions[0].superType}`,
      })
    }
  }, [selectedResolution, curResolutionOptions])

  useEffect(() => {
    setResolution()
  }, [form, curResolutionOptions])

  /** 默认选中第一个模型主题 */
  useEffect(() => {
    if (
      curModelThemeOptions.findIndex(
        item => item.name === selectModelTheme?.[0]?.name
      ) === -1
    ) {
      setSelectModelTheme([
        {
          name: curModelThemeOptions?.[0]?.name,
          modelCode: curModelThemeOptions?.[0]?.modelCode,
        },
      ])
    }
  }, [curModelThemeOptions])

  // 填充chatgpt描述
  const onGptResult = (value: string) => {
    setGptContent(value)
    form.setFieldValue('prompt', value.slice(0, 1000))
    form.validateFields(['prompt'])
    // alert(form.getFieldValue('prompt'))
  }

  const isSupportLoRA = useMemo(() => {
    if (!!options.patternModelOptions?.length) {
      return !!options.patternModelOptions.find(
        item => item.modelCode === curModelCode
      )?.supportLoRA
    }
    return !!modelThemeOptions.find(item => item.modelCode === curModelCode)
      ?.supportLoRA
  }, [modelThemeOptions, curModelCode, options])

  /** 创意度 */
  const modelCode2RecommendedCreativityDegree = useMemo(() => {
    let ret: Record<number, number> = {}
    options.modelSupportOptions.forEach(i => {
      i.modelSupport.forEach(j => {
        ret[j.modelCode!] = j.recommendedCreativityDegree || 50
      })
    })
    modelThemeOptions.map(i => {
      if (i.modelCode) {
        ret[i.modelCode] = i.recommendedCreativityDegree || 50
      }
    })
    options.patternModelOptions?.forEach(i => {
      ret[i.modelCode!] = i.recommendedCreativityDegree!
    })
    return ret
  }, [options, modelThemeOptions])
  // 如果用户没用改动过创意度，以服务端返回的默认相似度为准
  const creativityDegree = creativityDegreeTouched.current
    ? _creativityDegree
    : modelCode2RecommendedCreativityDegree[curModelCode] || 50

  /** 风格选择选项变了，过滤掉当前选中的，但是不在风格选择选项中的项目 */
  useEffect(() => {
    setStyleDecoration(
      styleDecoration.filter(
        it => styleDecorationOptions.findIndex(s => s.key === it.key) > -1
      )
    )
  }, [styleDecorationOptions])

  const getExpectedSecondsAction = useImperativeQuery<
    getExpectedSeconds,
    getExpectedSecondsVariables
  >(GET_EXPECTED_SECONDS)

  const onRefreshExpecteds = () => {
    getExpectedSecondsAction({
      modelCode: curModelCode,
    })
      .then(res => {
        if (res.data.pictureGraph?.aiArtworkTotalInfo.expectedSeconds) {
          setExpectedSeconds(
            res.data.pictureGraph?.aiArtworkTotalInfo.expectedSeconds
          )
        }
      })
      .catch(() => {})
  }

  /** 再来一张 */
  const again = async () => {
    const aiParams: any = getStorage('aiParam')
    if (!aiParams.prompt && !aiParams.initImageUrl) {
      return
    }

    let initImageUrl: any
    let width: any
    let height: any
    if (pattern === AiCreateArtworkPattern.GENERAL) {
      initImageUrl = aiParams.initImageUrl
        ? [{ url: aiParams.initImageUrl, status: 'done' }]
        : []
    } else {
      initImageUrl = aiParams.initImageUrl
      width = aiParams.resolution.split('*')[0]
      height = aiParams.resolution.split('*')[1]
    }

    let willSetParams = {
      character: aiParams.character,
      prompt: aiParams.prompt,
      size: aiParams.size,
      resolution: aiParams.resolution,
      initImageUrl: initImageUrl,
      ucPrompt: aiParams.ucPrompt,
      seed: aiParams.seed,
      cfg: aiParams.cfg,
      modelCodeForPattern: aiParams.modelCode,
      pretreatmentMethod: aiParams.pretreatmentMethod,
      sampler:
        typeof aiParams?.samplerModelInfo?.samplerIndex === 'number'
          ? [aiParams?.samplerModelInfo?.samplerIndex]
          : [],
      // samplerstep: aiParams?.samplerModelInfo?.steps,
      controlnetPrefine: aiParams.superType,
      trackEventLabel: aiParams.trackEventLabel,
    }

    if (controlnetPatternArr.includes(pattern)) {
      prefineOptionsQuery({
        width: Number(width || 0),
        height: Number(height || 0),
      }).then(res => {
        setOptions(__options => {
          return {
            ...__options,
            controlPrefineOption:
              res?.data?.pictureGraph?.aiCreateArtworkOptionMenu
                ?.controlPrefineOption,
          } as getAIMenu_pictureGraph_aiCreateArtworkOptionMenu
        })
      })
    }

    console.log(willSetParams, 'willSetParams')

    // 如果在涂抹模式下，将当前图带入待涂抹的图, 同时清空prompt, 不要改变model
    if (pattern === AiCreateArtworkPattern.MASK) {
      willSetParams.prompt = ''
      setMaskImageUrl(aiParams.originUrl)
    }

    // 手机照改漫Tab生成的图片没有size，根据底图为其匹配size
    if (aiParams.createSource === 1 && !willSetParams.size) {
      willSetParams.size = await getBestSizeForImage(aiParams.initImageUrl)
    }

    form.setFieldsValue(willSetParams)

    setSelectModel(aiParams.model)
    setCreativityDegree(aiParams.creativityDegree ?? DEFAULT_CREATIVITY_DEGREE)
    setPromptOptmize(aiParams.promptOptimize || false)
    setModelFusion(aiParams.modelFusion || [])
    setStyleDecoration(aiParams.styleDecorationOptions || [])
    setSelectModelTheme(aiParams.modelTheme || [])

    /** 如果再来一张的图是用了修手 */
    if (aiParams.doRepairTheHand) {
      setDoRepairTheHand(true)
    } else {
      setDoRepairTheHand(false)
    }

    if (pattern === AiCreateArtworkPattern.GENERAL) {
      setHasAdvance(
        !!aiParams.initImageUrl ||
          !!aiParams.modelFusion?.length ||
          !!aiParams.character?.length ||
          !!aiParams.cfg ||
          !!aiParams.seed ||
          !!aiParams.ucPrompt ||
          !!aiParams.samplerModelInfo
      )
      if (!aiParams.initImageUrl) {
        creativityDegreeTouched.current = false
      }
    } else if (pattern === AiCreateArtworkPattern.DEPTH2IMG) {
      setHasAdvance(
        !!aiParams.ucPrompt ||
          (aiParams.creativityDegree &&
            aiParams.creativityDegree !==
              modelCode2RecommendedCreativityDegree[lockModelCode!]) ||
          !!aiParams.cfg ||
          !!aiParams.seed ||
          !!aiParams.samplerModelInfo
      )
    } else if (controlnetPatternArr.includes(pattern)) {
      setHasAdvance(
        !!aiParams.ucPrompt ||
          (aiParams.creativityDegree &&
            aiParams.creativityDegree !==
              modelCode2RecommendedCreativityDegree[aiParams.modelCode]) ||
          !!aiParams.cfg ||
          !!aiParams.seed ||
          !!aiParams.samplerModelInfo
      )
    }

    clearStorage('aiParam')
  }

  const sizeQuery = useImperativeQuery<
    getAIModelcodeSizes,
    getAIModelcodeSizesVariables
  >(GET_AI_MODELCODE_SIZES)

  const onRefreshSizes = () => {
    sizeQuery({
      modelCode: curModelCode,
    }).then(res => {
      setOptions(__options => {
        return {
          ...__options,
          ddSize: res?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.ddSize,
          sdSize: res?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.sdSize,
        } as getAIMenu_pictureGraph_aiCreateArtworkOptionMenu
      })
    })
  }

  const defaultOptionsQuery = useImperativeQuery<
    getAIModelcodeDefaultOptions,
    getAIModelcodeDefaultOptionsVariables
  >(GET_AI_MODELCODE_DEFAULT_OPTIONS)

  const onRefreshDefaultOptions = () => {
    defaultOptionsQuery({
      modelCode: curModelCode,
    }).then(res => {
      setOptions(__options => {
        form.setFieldValue(
          'cfg',
          res?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.modelParam?.cfg ||
            null
        )
        form.setFieldValue(
          'sampler',
          typeof res?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.modelParam
            ?.samplerIndex === 'number'
            ? [
                res?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.modelParam
                  ?.samplerIndex,
              ]
            : []
        )
        // form.setFieldValue(
        //   'samplerstep',
        //   res?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.modelParam
        //     ?.steps || 0
        // )

        return {
          ...__options,
          modelParam:
            res?.data?.pictureGraph?.aiCreateArtworkOptionMenu?.modelParam,
        } as getAIMenu_pictureGraph_aiCreateArtworkOptionMenu
      })
    })
  }

  useEffect(() => {
    const initWithCurModelCode = async () => {
      setAccelerateTimes(1)
      if (!!curModelCode) {
        await Promise.all([
          onRefreshExpecteds(),
          onRefreshDefaultOptions(),
          !controlnetPatternArr.includes(pattern) ? onRefreshSizes() : null,
        ]).then(() => {
          isOptionsRendered.current = true
          isOptionsRenderedForHomepage.current = true
        })
      }
    }
    // reset侧边栏再来一张的render状态
    isOptionsRenderedForHomepage.current = false
    initWithCurModelCode()
  }, [curModelCode])

  /** 为了兼容AI作品详情页过来的再来一张 */
  useEffect(() => {
    const aiParams: any = getStorage('aiParam')
    if (!aiParams.modelCode) return
    setSelectModelTheme(
      modelThemeOptions.filter(it => it.modelCode === aiParams.modelCode)
    )
  }, [])

  useEffect(() => {
    if (!isOptionsRendered.current) return
    setTimeout(() => {
      again()
    }, 300)
  }, [isOptionsRendered.current])

  useEffect(() => {
    if (!isOptionsRenderedForHomepage.current || !store.ai?.isAgain) return
    const aiParams: any = getStorage('aiParam')
    if (!aiParams.modelCode) return
    setSelectModelTheme(
      modelThemeOptions.filter(it => it.modelCode === aiParams.modelCode)
    )
  }, [isOptionsRenderedForHomepage.current, store.ai?.isAgain])

  useEffect(() => {
    if (store.ai?.isAgain) {
      isOptionsRenderedForHomepage.current = true
    }
  }, [store.ai?.isAgain])

  useEffect(() => {
    if (!isOptionsRenderedForHomepage.current) return
    if (store.ai?.isAgain) {
      setTimeout(() => {
        again()
      }, 300)
      store.ai.setIsAgain(false)
    }
  }, [store.ai?.isAgain, isOptionsRenderedForHomepage.current])

  /** 改变作画次数，更新加速积分 */
  useEffect(() => {
    setAccelerateTimes(paintTimes)
  }, [paintTimes])

  useEffect(() => {
    /** 本次生成消耗的积分 */
    let ret = 0
    // 加速消耗
    if (accelerate) {
      ret += Math.max(accelarateTimes - memberAccelerateTimes, 0) * 2
    }
    // 大尺寸消耗
    if (curResolutionOptions.length !== 0) {
      const index = curResolutionOptions.findIndex(
        item =>
          `${item.length}*${item.width}*${item.superType}` ===
          selectedResolution
      )
      if (index !== -1) {
        const point = curResolutionOptions[index].price
        ret += point * paintTimes
      }
    }

    const curPrefineOptions = controlPrefineOptions?.filter(
      it => it.value === selectedControlPrefine
    )?.[0]
    if (!!curPrefineOptions?.price) {
      ret += curPrefineOptions?.price * paintTimes
    }

    /** 可用作画次数 */
    const availablePaintTimes = isDD ? payPaintTimes : totalPaintTimes
    ret += Math.max(paintTimes - availablePaintTimes, 0) * 2

    store.ai?.setComsumedPoints(ret)
  }, [
    accelerate,
    accelarateTimes,
    memberAccelerateTimes,
    curResolutionOptions,
    selectedResolution,
    paintTimes,
    payPaintTimes,
    totalPaintTimes,
    isDD,
    controlPrefineOptions,
  ])

  /** 当上传涂抹图时，为其匹配一个最合适的尺寸 */
  const matchSizeForPaintImage = (
    ratio: number,
    forceRender: boolean = true
  ) => {
    let best: any
    let delta = 10000
    curSizeOptions.map(item => {
      const [w, h] = item.name.split(':')
      const _ratio = Number(w) / Number(h)
      const _delta = Math.abs(_ratio - ratio)
      if (_delta < delta) {
        delta = _delta
        best = item
      }
    })
    if (forceRender) {
      form.setFieldsValue({ size: best.name })
    } else {
      setInitImageSize(best.name as string)
    }
  }

  const onInitImageUrlChange = (values: MyUploadFile[]) => {
    if (values?.[0]?.status === 'done') {
      const img = new Image()
      img.src = values?.[0]?.url!
      img.onload = () => {
        matchSizeForPaintImage(img.naturalWidth / img.naturalHeight, false)
      }
    }
  }

  const getBestSizeForImage = async (url?: string) => {
    if (!url) return
    const p = new Promise<string>(resolve => {
      const img = new Image()
      img.src = url
      img.onload = () => {
        const ratio = img.naturalWidth / img.naturalHeight
        let best: any
        let delta = 10000
        curSizeOptions.map(item => {
          const [w, h] = item.name.split(':')
          const _ratio = Number(w) / Number(h)
          const _delta = Math.abs(_ratio - ratio)
          if (_delta < delta) {
            delta = _delta
            best = item
          }
        })
        resolve(best.name)
      }
    })
    return p
  }

  const prefineOptionsQuery = useImperativeQuery<
    getControlnetPrefineOptions,
    getControlnetPrefineOptionsVariables
  >(GET_CONTROLNET_PREFINE_OPTIONS)

  const getImageSize = ({
    width,
    height,
  }: {
    width: number
    height: number
  }) => {
    form.setFieldsValue({
      initWidth: width,
      initHeight: height,
    })
    prefineOptionsQuery({
      width,
      height,
    }).then(res => {
      setOptions(__options => {
        return {
          ...__options,
          controlPrefineOption:
            res?.data?.pictureGraph?.aiCreateArtworkOptionMenu
              ?.controlPrefineOption,
        } as getAIMenu_pictureGraph_aiCreateArtworkOptionMenu
      })
    })
  }

  /** 画面描述placeholder */
  const promptPlaceholder = useMemo(() => {
    if (
      [AiCreateArtworkPattern.DEPTH2IMG, ...controlnetPatternArr].includes(
        pattern
      )
    ) {
      return '输入画面描述，支持中英文等语言输入'
    }
    if (pattern === AiCreateArtworkPattern.MASK) {
      return '输入画面描述生成的内容会替代涂抹区域的画面，当未输入或只输入“修复”两个字会将涂抹区域进行自动修复'
    }
    if (selectModel === AiCreateArtworkModel.ANIME_DIFFUSION) {
      return '画面描述以短句、短语为佳，高级设置内的参数会对图像生成有艺术修饰作用；支持中英文等语言输入'
    }
    return '画面描述以短句、短语为佳，画面类型和高级设置内的参数会对图像生成有艺术修饰作用；支持中英文等语言输入'
  }, [selectModel, pattern])

  return (
    <>
      <MyForm layout="vertical" form={form} $dark className={styles.darkForm}>
        {[AiCreateArtworkPattern.DEPTH2IMG, ...controlnetPatternArr].includes(
          pattern
        ) && (
          <MyForm.Item
            name="initImageUrl"
            label="上传图片"
            rules={[{ required: true }]}
            className={classNames(styles.borderLessFormItem, '!mb-8')}
          >
            <PictureCardUpload
              className={classNames(styles.customUpload, 'relative z-10')}
              needHash={false}
              limit={10}
              height={326}
              icon={
                <img
                  src={toCDNImage('/images/ai/image.png')}
                  alt=""
                  className="w-20 h-20 block mx-auto mb-2.5"
                />
              }
              label={
                <>
                  <div className="text-sm text-neutral-500 font-semibold mb-2.5">
                    拖拽图片到这里，或点击上传
                  </div>
                  <div className="text-xs text-neutral-400">
                    支持图片格式：JPG,PNG，Max：10MB
                  </div>
                </>
              }
              showInfo={false}
              showDelete
              onDelete={() => {
                form.setFieldValue('initImageUrl', null)
              }}
              getImageSize={getImageSize}
            ></PictureCardUpload>
          </MyForm.Item>
        )}

        {/* scribble 预处理方式 */}
        {[AiCreateArtworkPattern.SCRIBBLE].includes(pattern) && (
          <MyForm.Item
            label={
              <span>
                预处理方式{' '}
                <Tooltip
                  placement="topLeft"
                  arrowPointAtCenter
                  title="假涂鸦可以生成涂鸦和上色图，线稿涂鸦可以生成线稿图和上色图，无处理建议上传的是线稿图"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
            name="pretreatmentMethod"
            className={classNames(styles.borderLessFormItem, '!mb-3')}
            initialValue={preProcessOptions[0].id}
            rules={[{ required: true }]}
          >
            <PreProcessSelect />
          </MyForm.Item>
        )}

        {/* controlnet 除 scribble 外的预处理方式 */}
        {pattern !== AiCreateArtworkPattern.SCRIBBLE &&
          controlnetPatternArr.includes(pattern) && (
            <MyForm.Item
              required
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 6 }}
              label={
                <span>
                  图片需要预处理{' '}
                  <Tooltip
                    placement="topLeft"
                    arrowPointAtCenter
                    title="图片需要预处理会先生成预处理图，再生成结果图，如果你上传的就是一张预处理图，不需要预处理可直接生成结果图"
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </span>
              }
              name="pretreatmentMethod"
              className={classNames(
                styles.borderLessFormItem,
                '!mb-3',
                styles.inlineFormItem
              )}
            >
              <PreProcessSwitch />
            </MyForm.Item>
          )}

        {/* 当前模式下的模型选择 */}
        {controlnetPatternArr.includes(pattern) && (
          <MyForm.Item
            name="modelCodeForPattern"
            label="模型主题"
            rules={[{ required: true, message: '请选择模型主题' }]}
            className={classNames(styles.borderLessFormItem, '!mb-8')}
            initialValue={options.patternModelOptions?.[0].modelCode}
          >
            <ModelCodeSelect options={options.patternModelOptions ?? []} />
          </MyForm.Item>
        )}

        {/* dd sd ad选择 */}
        {[AiCreateArtworkPattern.GENERAL].includes(pattern) && (
          <MyForm.Item
            label={
              <span className="font-[SimSun]">
                * 模型选择{' '}
                <Tooltip
                  placement="topLeft"
                  arrowPointAtCenter
                  title="通用模型的生成速度很快，逻辑性强；场景模型的生成时间较长，更擅长恢弘的色彩想象"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
            className={classNames(styles.borderLessFormItem, '!mb-8')}
          >
            <Radio.Group
              className={styles.customRadioGroup}
              onChange={e => {
                setSelectModel(e.target.value)
              }}
              value={selectModel}
            >
              {modelOptions.map(item => {
                return (
                  <Radio
                    value={item?.model}
                    key={item?.model}
                    className={classNames(styles.customRadio)}
                  >
                    <div
                      className={classNames(
                        'flex relative items-center justify-between cursor-pointer py-3 px-4 h-[104px] border-2 border-solid border-neutral-200 rounded-xl flex-1 bg-neutral-200',
                        styles.radioBtn,
                        styles.modelBg
                      )}
                    >
                      {item.tag && (
                        <div className="absolute top-1 right-1 p-1 rounded-full text-center bg-error text-neutral-900 text-[12px] leading-none scale-[0.83]">
                          {item.tag}
                        </div>
                      )}
                      <div
                        className={classNames('flex-1 transition-all', {
                          'text-center': selectModel !== item.model,
                        })}
                      >
                        <div className="text-neutral-800 font-semibold">
                          {item?.name}
                        </div>
                        <div className="text-neutral-500 text-xs">
                          {item?.desc}
                        </div>
                      </div>
                      {selectModel === item.model && (
                        <div className="w-20 h-20">
                          <img src={item.url || ''} alt={item.name!} />
                        </div>
                      )}
                    </div>
                  </Radio>
                )
              })}
            </Radio.Group>
          </MyForm.Item>
        )}
        {[
          AiCreateArtworkPattern.GENERAL,
          AiCreateArtworkPattern.DEPTH2IMG,
          ...controlnetPatternArr,
        ].includes(pattern) && (
          <Prompt
            prompts={prompts}
            onRefreshPrompts={onRefreshPrompts}
            promptOptimize={promptOptimize}
            setPromptOptmize={setPromptOptmize}
            selectModel={selectModel}
            toTagGen={() => {
              Router.push('/tag-generator')
            }}
            pattern={pattern}
            onResult={onGptResult}
            gptVisibleState={gptVisibleState}
            setGptVisibleState={setGptVisibleState}
          />
        )}
        {/** 图像精绘 */}
        {controlnetPatternArr.includes(pattern) && (
          <MyForm.Item
            label={
              <>
                <span className="mr-1">图像精绘</span>
              </>
            }
            name="controlnetPrefine"
            rules={[{ required: true }]}
            className={styles.borderLessFormItem}
            initialValue={controlPrefineOptions?.[0]?.value}
          >
            <Radio.Group
              className={classNames(
                styles.customRadioGroup,
                styles.customPrefineRadio,
                '!mb-8'
              )}
            >
              {controlPrefineOptions.map(item => {
                return (
                  <Radio
                    key={item.label}
                    value={item.value}
                    className={styles.customRadio}
                  >
                    <div
                      className={classNames('radioWrap !rounded-full', {
                        prefine: item.value === SuperType.PREFINE,
                      })}
                    >
                      <div className="bg-neutral-100 rounded-full">
                        <div
                          className={classNames(
                            'flex items-center justify-center cursor-pointer p-2 border-2 border-solid border-neutral-200 rounded-full duration-75 flex-1 text-center text-xs w-full bg-neutral-200'
                          )}
                        >
                          <div className="text-neutral-800 font-semibold">
                            {item.label}
                            {!!item.price && (
                              <span className="text-neutral-500">
                                （{item.price}积分/张）
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Radio>
                )
              })}
            </Radio.Group>
          </MyForm.Item>
        )}

        {pattern === AiCreateArtworkPattern.MASK && (
          <>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-1">
                <div className="text-neutral-500 text-xs font-[SimSun]">
                  * 上传图片{' '}
                  <Tooltip
                    placement="topLeft"
                    arrowPointAtCenter
                    title="对上传图片涂抹区域较大时，图像的生成效果会更好，过小涂抹区域可能会无效，小区域涂抹画面描述适合输入“修复”进行图像自动擦除修复"
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
              </div>

              <PaintWithUpload
                ref={paintRef}
                maskImageUrl={maskImageUrl}
                matchSizeForPaintImage={matchSizeForPaintImage}
              />
            </div>

            <MyForm.Item
              label={
                <span>
                  画面描述{' '}
                  <Tooltip
                    placement="topLeft"
                    arrowPointAtCenter
                    title="画面尺寸参数指生成图片的最终大小，其它参数只对涂抹的区域图像有艺术修饰作用"
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </span>
              }
              name="prompt"
              className={classNames(styles.borderLessFormItem, '!mb-8')}
            >
              <InputArea
                onRefresh={() => onRefreshPrompts()}
                prompts={prompts}
                promptOptimize={promptOptimize}
                setPromptOptimize={setPromptOptmize}
                showSuggest={false}
                placeholder={promptPlaceholder}
              />
            </MyForm.Item>
          </>
        )}

        {/* 画面大小和resolution */}
        {!controlnetPatternArr.includes(pattern) && (
          <>
            <MyForm.Item
              label={
                <span>
                  画面大小{' '}
                  <Tooltip
                    placement="topLeft"
                    arrowPointAtCenter
                    title="人物类的图片推荐使用 1:1的方图，景观类的图片推荐使用其他比例的尺寸"
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </span>
              }
              name="size"
              rules={[{ required: true, message: '请选择画面大小' }]}
              className={classNames(styles.borderLessFormItem, '!mb-1')}
            >
              <Radio.Group className={styles.customRadioGroup}>
                {curSizeOptions.map(item => {
                  return (
                    <Radio
                      value={item?.name}
                      key={item?.name}
                      className={styles.customRadio}
                    >
                      <div
                        className={classNames(
                          'flex flex-col items-center justify-center cursor-pointer border-2 border-solid border-neutral-200 rounded-xl duration-75  flex-1 p-3 bg-neutral-200 w-full',
                          styles.radioBtn
                        )}
                      >
                        <img
                          alt=""
                          src={item?.url}
                          className="w-[30px] h-[30px] block"
                        />
                        <div className="text-neutral-800 font-semibold mt-2 !leading-none">
                          {item?.name}
                        </div>
                        <div className="text-neutral-500 text-[12px] mt-1 !leading-none">
                          {item?.desc}
                        </div>
                      </div>
                    </Radio>
                  )
                })}
              </Radio.Group>
            </MyForm.Item>
            <MyForm.Item name="resolution" rules={[{ required: true }]} noStyle>
              <Radio.Group
                className={classNames(
                  styles.customRadioGroup,
                  styles.customPrefineRadio,
                  '!mb-8'
                )}
              >
                {curResolutionOptions.map(item => {
                  return (
                    <Radio
                      key={`${item?.length}*${item?.width}*${item?.superType}`}
                      value={`${item?.length}*${item?.width}*${item?.superType}`}
                      className={styles.customRadio}
                    >
                      <div
                        className={classNames('radioWrap', {
                          prefine: item.superType === SuperType.PREFINE,
                        })}
                      >
                        <div className="bg-neutral-100 rounded-full">
                          <div
                            className={classNames(
                              'flex items-center justify-center cursor-pointer p-2 border-2 border-solid border-neutral-200 rounded-full duration-75 flex-1 text-center text-xs w-full bg-neutral-200'
                            )}
                          >
                            <div className="text-neutral-800 font-semibold">
                              {`${item?.length}*${item?.width}`}
                              {!!item.price && (
                                <span className="text-neutral-500">
                                  （{item.price}积分/张）
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Radio>
                  )
                })}
              </Radio.Group>
            </MyForm.Item>
          </>
        )}

        {pattern === AiCreateArtworkPattern.GENERAL && (
          <MyForm.Item
            label={
              <span>
                <span className="text-neutral-500 font-[SimSun]">
                  * 模型主题{' '}
                </span>
                <Tooltip
                  placement="topLeft"
                  arrowPointAtCenter
                  title="通过模型主题设定，可以设定最终生成出来的画面是什么材质和风格"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
            className={classNames(styles.borderLessFormItem, '!mb-8')}
          >
            <TiledSelect
              current={selectModelTheme}
              setCurrent={setSelectModelTheme}
              options={modelThemeOptions}
              curOptions={curModelThemeOptions}
              selectModel={selectModel}
              setSelectModel={setSelectModel}
            />
          </MyForm.Item>
        )}
        {[AiCreateArtworkPattern.GENERAL, AiCreateArtworkPattern.MASK].includes(
          pattern
        ) && (
          <MyForm.Item
            label={
              <span>
                风格选择{' '}
                <Tooltip
                  placement="topLeft"
                  arrowPointAtCenter
                  title="包含风格修饰、艺术家、元素魔法，支持多选"
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
            className={classNames(styles.borderLessFormItem, '!mb-8')}
          >
            <StyleDecorationSelect
              options={styleDecorationOptions}
              current={styleDecoration}
              setCurrent={setStyleDecoration}
            />
          </MyForm.Item>
        )}
        {pattern !== AiCreateArtworkPattern.MASK && (
          <div className="flex items-center justify-between mb-8">
            <span className="mr-3 text-neutral-800 text-base">高级设置</span>
            <MySwitch
              checked={hasAdvance}
              onChange={checked => {
                setHasAdvance(checked)
              }}
            />
          </div>
        )}

        {hasAdvance && (
          <>
            {pattern === AiCreateArtworkPattern.GENERAL && (
              <>
                <MyForm.Item
                  label={
                    <span>
                      参考图{' '}
                      <Tooltip
                        placement="topLeft"
                        arrowPointAtCenter
                        title="你可以上传一张参考图，参考图可以上传你原创的图片，或是使用无界AI创作功能生成的图片，或在无界版图拥有改编权的图片。最终生成画面将参考这张图进行，具体的方式为将其作为初始化画面，在此基础上进行迭代，最终画面会结合参考图和描述文本。"
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  name="initImageUrl"
                  className={classNames(styles.borderLessFormItem, '!mb-3')}
                >
                  <MultiFileUpload
                    canRename={false}
                    maxToken={1}
                    multiple={false}
                    dirname="ai_init_image"
                    onChange={onInitImageUrlChange}
                    className="h-[162px]"
                  />
                </MyForm.Item>
                {selectInitImageUrl?.length > 0 && (
                  <>
                    <div className="flex items-center mb-2.5 -mt-4">
                      <span
                        className={classNames('text-neutral-500', {
                          'w-[130px]': show2dDensity,
                          'w-[120px]': !show2dDensity,
                        })}
                      >
                        {show2dDensity ? '二次元强度' : '创意度'}：
                        {creativityDegree}%
                      </span>
                      <MySlider
                        className="flex-1 ml-4"
                        step={5}
                        value={creativityDegree}
                        onChange={val => {
                          setCreativityDegree(val)
                        }}
                        tooltip={{
                          formatter: val => {
                            return `${val}%`
                          },
                        }}
                      />
                    </div>
                    {/* <MyForm.Item
                      className={classNames(styles.borderLessFormItem, '!mb-0')}
                      label={
                        <span>
                          降噪{' '}
                          <Tooltip
                            placement="topLeft"
                            arrowPointAtCenter
                            title="通过降噪（Denoising）增加噪点的强度，强度越高AI创作空间越大，创作和原图差距越大"
                          >
                            <QuestionCircleOutlined />
                          </Tooltip>
                        </span>
                      }
                      name="denoising"
                    >
                      <Stepper
                        step={0.05}
                        min={0}
                        max={1}
                        digits={2}
                        className="absolute right-0 -top-7"
                      />
                    </MyForm.Item> */}
                  </>
                )}
                {initImageSize &&
                  initImageSize !== selectSize &&
                  selectInitImageUrl?.length > 0 && (
                    <div className="text-xs text-neutral-500 py-2 px-4 mb-3 bg-neutral-200 rounded-xl">
                      建议画面大小选择{initImageSize}效果更佳
                    </div>
                  )}
                {!show2dDensity &&
                  creativityDegree <= 20 &&
                  selectInitImageUrl?.length > 0 && (
                    <div className="text-xs text-neutral-500 py-2 px-4 mb-3 bg-neutral-200 rounded-xl">
                      生成的新图与底图创意度低于20%，在没有获得底图改编权的情况下，您用于商业用途可能侵权，但不影响您用于交流学习
                    </div>
                  )}
                <div className="mb-7"></div>
              </>
            )}

            {/* 负面描述 */}
            {[
              AiCreateArtworkPattern.DEPTH2IMG,
              ...controlnetPatternArr,
              AiCreateArtworkPattern.GENERAL,
            ].includes(pattern) && (
              <>
                <MyForm.Item
                  label={
                    <span>
                      负面描述{' '}
                      <Tooltip
                        placement="topLeft"
                        arrowPointAtCenter
                        title="负面描述填写你不想在创作中加入的东西"
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  name="ucPrompt"
                  className={classNames(styles.borderLessFormItem, '!mb-8')}
                >
                  <InputArea
                    showSuggest={false}
                    placeholder={'请输入不想出现在画面中的内容，支持中英文输入'}
                    className={styles.colorLessTextArea}
                  />
                </MyForm.Item>
                {!!options.inWhiteList &&
                  typeof options.modelParam?.samplerIndex === 'number' && (
                    <>
                      <MyForm.Item
                        className={classNames(
                          styles.borderLessFormItem,
                          '!mb-8'
                        )}
                        label={
                          <span>
                            采样模式{' '}
                            <Tooltip
                              placement="topLeft"
                              arrowPointAtCenter
                              title="采样模式（Sampler）是指扩散去噪算法的采样模式，如果设置正确，它们会发散并最终收敛"
                            >
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </span>
                        }
                        name="sampler"
                      >
                        <GridSelect
                          placeholder="请选择采样模式"
                          options={
                            options?.samplerOptions as getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_samplerOptions[]
                          }
                          max={1}
                        />
                      </MyForm.Item>
                      {/* {!!selectSampler?.length && (
                        <MyForm.Item
                          className={classNames(
                            styles.borderLessFormItem,
                            '!mb-0'
                          )}
                          label="采样步骤"
                          name="samplerstep"
                        >
                          <Stepper
                            className="absolute right-0 -top-7"
                            step={1}
                            min={1}
                            max={150}
                          />
                        </MyForm.Item>
                      )} */}
                    </>
                  )}
                {!!options.inWhiteList && (
                  <MyForm.Item
                    label={
                      <span>
                        随机种子{' '}
                        <Tooltip
                          placement="topLeft"
                          arrowPointAtCenter
                          title="通过随机种子（Seed）确定扩散的初始状态，进而控制生成的样本的多样性"
                        >
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                    name="seed"
                    className={classNames(styles.borderLessFormItem, '!mb-8')}
                  >
                    <Input
                      placeholder={'请输入随机种子'}
                      type="number"
                      min={1}
                      allowClear
                      className={styles.colorLessInput}
                      onChange={e => {
                        if (!!e.target.value) {
                          const _val = Math.floor(Number(e.target.value))
                          form.setFieldValue('seed', _val <= 0 ? 1 : _val)
                        }
                      }}
                    />
                  </MyForm.Item>
                )}
                {!!options.modelParam?.cfg && (
                  <MyForm.Item
                    label={
                      <span>
                        提示词相关性{' '}
                        <Tooltip
                          placement="topLeft"
                          arrowPointAtCenter
                          title="提示词相关性（CFG scale）表示AI对描述参数的倾向程度"
                        >
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                    name="cfg"
                    className={classNames(styles.borderLessFormItem, '!mb-0')}
                  >
                    <Stepper
                      className="absolute right-0 -top-7 w-[136px]"
                      step={0.05}
                      min={1}
                      max={30}
                      digits={2}
                    />
                  </MyForm.Item>
                )}
              </>
            )}

            {isSupportLoRA &&
              [
                ...controlnetPatternArr,
                AiCreateArtworkPattern.GENERAL,
              ].includes(pattern) && (
                <MyForm.Item
                  label={
                    <span>
                      角色同人{' '}
                      <Tooltip
                        placement="topLeft"
                        arrowPointAtCenter
                        title="创作中会加入选中的角色同人特征；选项名称前有（负向）时，创作会规避该角色同人的特征"
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  name="character"
                  className={classNames(styles.borderLessFormItem, '!mb-4')}
                  extra={
                    <div className="absolute right-0 -top-6">
                      <a
                        href="https://support.wujiebantu.com/qa/pt.html"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center text-xs pl-2 pr-0.5 rounded-3xl bg-[#281A54] text-primary"
                      >
                        使用说明
                        <MyIcon
                          icon="arrow-right-simple"
                          className="text-[20px]"
                        />
                      </a>
                    </div>
                  }
                >
                  <GridSelect
                    placeholder="请选择角色同人"
                    options={characterOptions}
                  />
                </MyForm.Item>
              )}

            {isSupportLoRA &&
              [
                ...controlnetPatternArr,
                AiCreateArtworkPattern.GENERAL,
              ].includes(pattern) && (
                <MyForm.Item
                  label={
                    <span>
                      融合模型{' '}
                      <Tooltip
                        placement="topLeft"
                        arrowPointAtCenter
                        title="将基础模型和其它模型融合，分别设置权重。模型权重取值在-1-2之间"
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  extra={
                    <div className="absolute right-0 -top-6">
                      <a
                        href="https://support.wujiebantu.com/qa/lora.html"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center text-xs pl-2 pr-0.5 rounded-3xl bg-[#281A54] text-primary"
                      >
                        使用说明
                        <MyIcon
                          icon="arrow-right-simple"
                          className="text-[20px]"
                        />
                      </a>
                    </div>
                  }
                  className={classNames(styles.borderLessFormItem, '!mb-0')}
                >
                  <ModelFusionSelect
                    current={modelFusion}
                    setCurrent={setModelFusion}
                    recommendedOptions={
                      options.recommendedModelFusionOptions as getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_recommendedModelFusionOptions[]
                    }
                    options={modelFusionOptions}
                  />
                </MyForm.Item>
              )}

            {/* 创意度 */}
            {[
              AiCreateArtworkPattern.DEPTH2IMG,
              ...controlnetPatternArr,
            ].includes(pattern) && (
              <div className="flex items-center mb-4">
                <span className={classNames('text-neutral-500 w-[120px]')}>
                  创意度：{creativityDegree}%{' '}
                  <Tooltip
                    placement="topLeft"
                    arrowPointAtCenter
                    title="创意度越低生成图片越接近参考图，创意度越高生成图片越有创意"
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </span>
                <MySlider
                  className="flex-1 ml-4"
                  step={5}
                  value={creativityDegree}
                  onChange={val => {
                    setCreativityDegree(val)
                  }}
                  tooltip={{
                    formatter: val => {
                      return `${val}%`
                    },
                  }}
                />
              </div>
            )}
          </>
        )}
        <section>
          <div className="flex items-center justify-between mb-4">
            <span className="text-neutral-500">
              作图数量{' '}
              <Tooltip
                placement="topLeft"
                arrowPointAtCenter
                title="作图数量会消耗同等数量的作画次数"
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
            <Stepper
              step={1}
              min={1}
              max={options.numCeiling || 100}
              value={paintTimes}
              onChange={setPaintTimes}
            />
          </div>
          <div
            className={classNames(
              'flex items-center justify-between cursor-pointer px-4 py-5 border-2 border-solid border-neutral-200 rounded-2xl duration-75 bg-neutral-200',
              {
                '!border-neutral-500': !accelerate,
                [styles.radialBg]: !accelerate,
              }
            )}
            onClick={() => setAccelerate(false)}
          >
            <div className="flex items-center">
              <span className="text-neutral-800 text-sm font-medium">
                免费生成
              </span>
            </div>
            <div className="text-neutral-500 text-xs">
              预计排队：
              {timeWithUint(
                expectedSeconds?.freeExpectedSeconds +
                  expectedSeconds.reduceTime * (paintTimes - 1)
              )}
            </div>
          </div>
          <div
            className={classNames(
              'mt-4  cursor-pointer px-4 py-5 border-2 border-solid border-neutral-200 rounded-2xl duration-75 bg-neutral-200',
              {
                '!border-neutral-500': accelerate,
                [styles.radialBg]: accelerate,
              }
            )}
            onClick={() => {
              if (validateIsLogin()) {
                setAccelerate(true)
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-neutral-800 text-sm font-medium">
                  加速生成{' '}
                  <Tooltip
                    placement="topLeft"
                    arrowPointAtCenter
                    title="使用积分/AI创作权益卡可加速生成作画，积分/权益卡加速次数越多速度越快；当加速至平台当前最快作画速度时，不可再增加积分/加速次数；权益卡加速次数不足情况下会额外消耗积分"
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </span>
              </div>
              <div
                className={classNames('text-neutral-500 text-xs', {
                  'text-success': accelerate,
                })}
              >
                {accelerate ? (
                  <>
                    预计排队：
                    {timeWithUint(
                      expectedSeconds?.proExpectedSeconds +
                        expectedSeconds?.reduceTime * (paintTimes - 1) -
                        (accelarateTimes * expectedSeconds?.reduceTime) /
                          paintTimes
                    )}
                  </>
                ) : (
                  '使用积分可加速作画'
                )}
              </div>
            </div>
            {accelerate && (
              <>
                <div className="w-full h-px bg-neutral-100 my-4"></div>
                <div className="flex items-center justify-between">
                  <span className="text-neutral-400 text-sm font-medium">
                    加速次数
                    {!memberAccelerateTimes
                      ? `（消耗${accelarateTimes * 2}积分）`
                      : `（权益卡剩余
              ${memberAccelerateTimes}次）`}
                  </span>
                  <Stepper
                    light
                    step={paintTimes}
                    min={paintTimes}
                    max={maxPoint}
                    value={accelarateTimes}
                    onChange={value => {
                      setAccelerateTimes(value)
                    }}
                  />
                </div>
              </>
            )}
          </div>

          <p className="text-xs text-neutral-500 mt-4 pb-8">
            使用无界AI创作服务表示您已同意
            <MyLink
              href="/us/ai-agreement"
              className="primary-color"
              exist={true}
            >
              《AI创作服务协议》
            </MyLink>
            <MyLink
              href="/us/ai-ad-agreement"
              className="primary-color"
              exist={true}
            >
              《二次元模型AI创作服务协议》
            </MyLink>
          </p>
        </section>
        <MyForm.Item name="trackEventLabel" hidden>
          <Input></Input>
        </MyForm.Item>
      </MyForm>
    </>
  )
}

export const AiForm = observer(forwardRef(AiFormMain))
